import type { AppRole } from "../types/supabase.types.gen";

const IMAGE_MIMETYPES = ["image/jpeg", "image/png"] as const;
export const PDF_MIMETYPES = ["application/pdf"] as const;
export const DOCUMENT_MIMETYPES = [...PDF_MIMETYPES, ...IMAGE_MIMETYPES] as const;
export const MICROSOFT_OFFICE_MIMETYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;
export const HEIC_MIMETYPES = ["image/heic", "image/heif"] as const;

export const BUSINESS_UNITS = [
  "CET",
  "TP / VRD",
  "Bâtiment & Gros oeuvre",
  "Télécom et énergies",
  "Constructions modulaires",
  "Management de projets",
  "Clos et couvert",
  "Bureau d'études",
  "Tertiaire",
  "Enveloppe du bâtiment",
] as const;

export const BOARD_MANAGERS_EMAILS = [
  "baptiste@asap.work",
  "emilie.vialle@asap.work",
  "mathias@asap.work",
  "steve@asap.work",
  "jimmy@asap.work",
  "julien.gaultier@asap.work",
  "francois.gouzon@asap.work",
  "florent.pasquiou@asap.work",
  "guenael@asap.work",
] as const;

export const CONTRACT_MINIMUM_AMOUNT = 10000;
export const EXTENSION_AMENDMENT_MAX_ALLOWED = 2;
export const ASAP_TT_COMPANY_ID = "4a5eb0aa-e92f-4b8b-8f8f-f2cdbd4337f9";

/**
 * Includes support roles except developer
 */
export const SUPPORT_ROLES: AppRole[] = [
  "team_member_independent_administrator",
  "csm_team_member",
  "accounting_team_member",
  "team_member_marketing",
] as const;

export const WORKSPACE_TYPES = ["internal", "external", "all"] as const;

export const DEFAULT_QUERY_LIMIT = 50;
export const SMALL_QUERY_LIMIT = 20;

export const SERVICE_DOMAINS_TO_FILTER = [
  "@accounts.google.com",
  "@aircall.io",
  "@apec.fr",
  "@asapwork.teamtailor-mail.com",
  "@attio.com",
  "@batiactu.com",
  "@broadbean.net",
  "@bytescout.com",
  "@calendly.com",
  "@canva.com",
  "@customer.io",
  "@dashlane.com",
  "@delighted.com",
  "@diffusion.apec.fr",
  "@discord.com",
  "@docuseal.co",
  "@docuseal.com",
  "@docs.google.com",
  "@e.fastt.org",
  "@e.linkedin.com",
  "@email.apple.com",
  "@emails.hellowork.com",
  "@evenements-bpifrance.fr",
  "@everping.eu",
  "@fastt.org",
  "@flutterflow.io",
  "@getjetadmin.com",
  "@google.com",
  "@hellowork.com",
  "@hubspot.com",
  "@hunter.io",
  "@icloud.com",
  "@illuca.net",
  "@ilucca.net",
  "@ilovepdf.com",
  "@in.frontapp.com",
  "@indeed.com",
  "@info.n8n.io",
  "@info.swile.co",
  "@invitations.mailinblack.com",
  "@jobijoba.com",
  "@jobijoba.io",
  "@kaspr.io",
  "@linkedin.com",
  "@livestormevents.com",
  "@login.customer.io",
  "@loom.com",
  "@lusha.com",
  "@mail.hunter.io",
  "@make.com",
  "@managerbox.fr",
  "@managercom.fr",
  "@marketing.zucchetti.com",
  "@mariaschools.com",
  "@md.getsentry.com",
  "@message.teamtailor.com",
  "@metabaseapp.com",
  "@meteojob.com",
  "@net.estia.fr",
  "@netlify.com",
  "@newsletter.hunter.io",
  "@newslettermanager.fr",
  "@noreply-pole-emploi.fr",
  "@noreply.getprimo.com",
  "@pole-emploi.fr",
  "@posthog.com",
  "@reach.lusha.com",
  "@rcibanque.com",
  "@recruteur.hellowork.com",
  "@sentry.com",
  "@send.calendly.com",
  "@signnow.com",
  "@slack.com",
  "@snowflake.com",
  "@spendesk.com",
  "@spokephone.com",
  "@supabase.io",
  "@team.twilio.com",
  "@tool4staffing.com",
  "@twilio.com",
  "@unleashgroup.io",
  "@wetransfer.com",
  "@yousign.app",
] as const;

export const PERSONAL_DOMAINS_TO_FILTER = [
  "@outlook.com",
  "@protonmail.com",
  "@aol.com",
  "@outlook.fr",
  "@hotmail.com",
  "@hotmail.fr",
  "@live.fr",
  "@laposte.net",
  "@yahoo.fr",
  "@yahoo.com",
  "@icloud.com",
  "@yahoo.com",
  "@googlemail.com",
  "@gmail.com",
  "@gmail.fr",
  "@gmx.fr",
  "@msn.com",
  "@wanadoo.fr",
  "@me.com",
  "@orange.fr",
  "@sfr.fr",
] as const;

export const ASAP_WORK_EMAIL_DOMAIN = ["@asap.work", "@menco-rh.fr"] as const;

export const SENSITIVE_KEYS = [
  "[*].email",
  "email",
  "[*].emails",
  "emails",
  "[*].rib",
  "rib",
  "[*].iban",
  "iban",
  "[*].bic",
  "bic",
  "[*].phone_number",
  "phone_number",
  "[*].phoneNumber",
  "phoneNumber",
  "[*].social_security_number",
  "social_security_number",
  "[*].socialSecurityNumber",
  "socialSecurityNumber",
  "[*].first_name",
  "first_name",
  "[*].firstName",
  "firstName",
  "[*].last_name",
  "last_name",
  "[*].lastName",
  "lastName",
  "[*].address",
  "address",
  "[*].city",
  "city",
  "[*].zip",
  "zip",
  "[*].bank",
  "bank",
  "[*].identityCard",
  "identityCard",
  "[*].identity_card",
  "identity_card",
  "[*].passport",
  "passport",
  "[*].passport_number",
  "passport_number",
  "[*].driverLicense",
  "driverLicense",
  "[*].residence_permit",
  "residence_permit",
  "[*].residencePermit",
  "residencePermit",
  "[*].password",
  "password",
  "[*].token",
  "token",
  "[*].secret",
  "secret",
  "[*].key",
  "key",
  "apiKey",
  "[*].apiKey",
] as const;

export const HUBSPOT_JOB_TITLES = [
  "PDG / Président",
  "DG",
  "Fondateur",
  "Directeur Achat",
  "Directeur Travaux",
  "DRH",
  "Directeur d'agence",
  "Directeur d'exploitation",
  "Directeur études",
  "Responsable bureau d'études",
  "Responsable maintenance",
  "Responsable de site",
  "Responsable d'exploitation",
  "Responsable d'activité",
  "RH",
  "Responsable recrutement",
  "Chef de groupe",
  "Chef de service",
  "Chef de secteur",
  "Responsable d'affaires",
  "Conducteur de travaux principal",
  "Conducteur de travaux",
  "Recruteur",
  "Chef de projet",
  "Chargé d'affaires",
  "Assistant",
  "Chef de chantier",
  "Chef d'équipe",
] as const;

export const MAXIMUM_DAYS_WORKED_WITHOUT_MEDICAL_CHECKUP = 30;

export const MISSION_DEFAULT_STATUS = "open";
export const MISSION_DEFAULT_NUMBER_OF_OPEN_POSITIONS = 1;
